import { useEffect, useMemo, useState, useContext } from 'react';
import { useLoaderData } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { SmartContext } from '../../../library/Core/SmartContext';
import smartReducer from '../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import DebugBarControl from '../../../library/SimpleControls/DebugBarControl';
import LoadingSpinner from '../../../library/SimpleControls/LoadingSpinner';
import { addUserSpecificDomains } from '../../../library/Service/domainService';
import MyEnquiriesTable from './components/MyEnquiriesTable';
import MyEnquiryDetailModal from './components/MyEnquiryDetailModal';
import MyEnquiriesService from './MyEnquiriesService';

const MyEnquiries = () => {
    const { globalState } = useContext(GlobalContext);
    const [selectedInquiry, setSelectedInquiry] = useState<any>(null);

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: true, showFormErrors: 0 },
        formValidationErrors: {},
        actions: {},
    } as any);

    const { formConfig, data, internal, routeInfo } = useLoaderData() as State;
    const domain = useMemo(() => addUserSpecificDomains(new Map([...globalState.domain]), data), [globalState.domain, data]);

    useEffect(() => {
        dispatch({
            type: 'DATA_INIT',
            payload: { formConfig, data, domain, internal, routeInfo },
        });
    }, []);

    const refreshInquiryList = async (inquiryId?: string | number) => {
        try {
            const response = await MyEnquiriesService.getMyEnquiries();
            const payload = response?.data ?? {};
            const inquiryList = Array.isArray(payload?.data)
                ? payload.data.map((item: any) => MyEnquiriesService.normalizeInquiry(item))
                : [];

            dispatch({
                type: 'UPDATE_CONTROL_VALUE',
                payload: { dataKey: 'inquiryList', value: inquiryList },
            });

            dispatch({
                type: 'UPDATE_CONTROL_VALUE',
                payload: {
                    dataKey: 'pagination',
                    value: {
                        ...(state.data?.pagination || { page: 1, limit: inquiryList.length }),
                        total: payload?.total ?? inquiryList.length,
                        limit: state.data?.pagination?.limit || inquiryList.length,
                    },
                },
            });

            if (inquiryId) {
                const refreshedInquiry = inquiryList.find((item: any) => `${item.id}` === `${inquiryId}`) || null;
                setSelectedInquiry(refreshedInquiry);
                return refreshedInquiry;
            }

            return inquiryList;
        } catch (error) {
            console.error('Error refreshing My Enquiries:', error);
            return null;
        }
    };

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <div className="main flex-1">
                {!state?.data && <LoadingSpinner />}
                {state?.data && (
                    <div className="bg-white py-5">
                        <div className="max-1140 mx-auto">
                            <div className="row g-3 align-items-start mb-4">
                                <div className="col">
                                    <h2 className="font-26 font-600 mb-1">{state.formConfig?.title || 'My Enquiries'}</h2>
                                    <p className="font-16 text-muted mb-0">{state.formConfig?.subTitle || 'Track enquiries you have sent to service providers.'}</p>
                                </div>
                            </div>

                            <MyEnquiriesTable onViewInquiry={setSelectedInquiry} />

                            <MyEnquiryDetailModal
                                inquiry={selectedInquiry}
                                show={!!selectedInquiry}
                                onHide={() => setSelectedInquiry(null)}
                                onInquiryRefreshed={(refreshedInquiry: any) => {
                                    if (!refreshedInquiry?.id) return;
                                    setSelectedInquiry(refreshedInquiry);
                                }}
                                onRequestRefresh={refreshInquiryList}
                            />
                        </div>
                    </div>
                )}
                <DebugBarControl />
            </div>
        </SmartContext.Provider>
    );
};

export default MyEnquiries;